// components/dashboard/EnquiryDashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getAllEnquiries,
  deleteEnquiry,
  markEnquiryAsRead,
} from "../../../Services/Services";
import EnquiryStats from "./StatCardss";
import EnquiryTable from "./EnquiryList";
import EnquiryDetailModal from "./EnquiryDetailsModal";

import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const EnquiryDashboard = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const itemsPerPage = 8;

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllEnquiries();
      if (data) {
        const results = Array.isArray(data) ? data : data.results || [];
        setEnquiries(results);
        setFilteredEnquiries(results);
      } else {
        setError("Failed to fetch enquiries. Please try again.");
      }
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  useEffect(() => {
    let result = [...enquiries];
    if (filterStatus === "read") result = result.filter((e) => e.is_read);
    else if (filterStatus === "unread")
      result = result.filter((e) => !e.is_read);

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (e) =>
          e.name?.toLowerCase().includes(lower) ||
          e.email?.toLowerCase().includes(lower) ||
          e.company?.toLowerCase().includes(lower) ||
          e.phone?.includes(lower),
      );
    }
    setFilteredEnquiries(result);
    setCurrentPage(1);
  }, [searchTerm, filterStatus, enquiries]);

  const handleViewEnquiry = async (enquiry) => {
    setSelectedEnquiry(enquiry);
    setIsModalOpen(true);
    if (!enquiry.is_read) {
      const updated = await markEnquiryAsRead(enquiry.id);
      if (updated) {
        setEnquiries((prev) =>
          prev.map((e) => (e.id === enquiry.id ? { ...e, is_read: true } : e)),
        );
        setSelectedEnquiry((prev) => ({ ...prev, is_read: true }));
      }
    }
  };

  const handleDeleteEnquiry = async (id) => {
    const result = await deleteEnquiry(id);
    if (result !== null) {
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      setDeleteConfirm(null);
      if (isModalOpen && selectedEnquiry?.id === id) {
        setIsModalOpen(false);
        setSelectedEnquiry(null);
      }
    }
  };

  const totalPages = Math.ceil(filteredEnquiries.length / itemsPerPage);
  const paginatedData = filteredEnquiries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const stats = {
    total: enquiries.length,
    unread: enquiries.filter((e) => !e.is_read).length,
    read: enquiries.filter((e) => e.is_read).length,
    today: enquiries.filter((e) => {
      const today = new Date().toDateString();
      return new Date(e.created_at).toDateString() === today;
    }).length,
  };

  return (
    <Box sx={{ maxWidth: 1400, width: "100%", p: 1, mt: -3 }}>
      {/* Stats */}
      <EnquiryStats stats={stats} />
      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          border: `1.5px solid ${T.border}`,
          borderRadius: "4px !important",
          p: 1.25,
          mb: 2,
          bgcolor: "transparent",
          boxShadow: "none",
        }}
      >
        <Grid container spacing={1} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, email, company or phone…"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: T.primary }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 13, color: T.textSub }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 34,
                  ...sq,
                  bgcolor: T.white,
                  transition: "box-shadow 0.2s",
                },
                "& fieldset": { border: `1.5px solid ${T.border}` },
                "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
                "& .Mui-focused fieldset": {
                  border: `1.5px solid ${T.primary}`,
                },
                "& .Mui-focused": {
                  boxShadow: `0 0 0 3px ${T.primaryLight}`,
                },
              }}
            />
          </Grid>

          {/* Reset */}
          <Grid item xs={4} sm={2} md={1}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
              }}
              sx={{ ...ghostBtn, height: 34 }}
            >
              Reset
            </Button>
          </Grid>

          {/* Filter Toggle */}
          <Grid item xs={8} sm={5} md={3.5}>
            <ToggleButtonGroup
              fullWidth
              value={filterStatus}
              exclusive
              onChange={(_, val) => val && setFilterStatus(val)}
              size="small"
              sx={{
                height: 34,
                bgcolor: T.white,
                border: `1.5px solid ${T.border}`,
                borderRadius: "4px !important",
                p: 0.25,
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "4px !important",
                  px: 1.5,
                  py: 0.6,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: T.textSub,
                  textTransform: "capitalize",
                  "&.Mui-selected": {
                    bgcolor: T.primaryBg,
                    color: T.primary,
                    "&:hover": {
                      bgcolor: T.primaryBg,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="unread">
                Unread
                {stats.unread > 0 && (
                  <Chip
                    label={stats.unread}
                    size="small"
                    color="error"
                    sx={{
                      ml: 0.6,
                      height: 16,
                      fontSize: "0.6rem",
                      "& .MuiChip-label": { px: 0.6 },
                    }}
                  />
                )}
              </ToggleButton>
              <ToggleButton value="read">Read</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Refresh */}
          <Grid item xs={2} sm={1.5} md={0.8}>
            <Tooltip title="Refresh" arrow>
              <Button
                fullWidth
                size="small"
                onClick={fetchEnquiries}
                disabled={loading}
                sx={{ ...ghostBtn, height: 34, minWidth: 0, px: 1 }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 15,
                    animation: loading ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </Button>
            </Tooltip>
          </Grid>

          {/* Spacer */}
          <Grid
            item
            xs={false}
            sm={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Results Count */}
          <Grid item xs={10} sm={4} md={2}>
            <Box
              sx={{
                height: 34,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: T.primaryBg,
                color: T.primary,
                border: `1.5px solid ${T.border}`,
                fontWeight: 700,
                fontSize: "0.72rem",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: T.primary,
                  lineHeight: 1,
                }}
              >
                {filteredEnquiries.length} Enquiries
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="error" size="small" onClick={fetchEnquiries}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Table */}
      <EnquiryTable
        enquiries={paginatedData}
        loading={loading}
        onView={handleViewEnquiry}
        onDelete={(id) => setDeleteConfirm(id)}
      />

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{" "}
            <strong>
              {Math.min(currentPage * itemsPerPage, filteredEnquiries.length)}
            </strong>{" "}
            of <strong>{filteredEnquiries.length}</strong> results
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, val) => setCurrentPage(val)}
            color="primary"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": { borderRadius: 1.5 },
            }}
          />
        </Box>
      )}

      {/* Detail Modal */}
      {isModalOpen && selectedEnquiry && (
        <EnquiryDetailModal
          enquiry={selectedEnquiry}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedEnquiry(null);
          }}
          onDelete={(id) => setDeleteConfirm(id)}
        />
      )}

      {/* Delete Confirm Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: "error.100",
              mx: "auto",
              mb: 1.5,
            }}
          >
            <DeleteOutlineIcon sx={{ color: "error.main", fontSize: 26 }} />
          </Avatar>
          <Typography variant="h6" fontWeight={700}>
            Delete Enquiry
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this enquiry? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setDeleteConfirm(null)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={() => handleDeleteEnquiry(deleteConfirm)}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnquiryDashboard;
